import {toast} from '@wandb/weave/common/components/elements/Toast';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import {cloneDeep} from 'lodash';
import React, {useCallback, useContext, useMemo} from 'react';

import {ViewSource} from '../../../generated/graphql';
import {useApolloClient} from '../../../state/hooks';
import {saveView} from '../../../state/reports/hooks';
import {RunQueryContext} from '../../../state/runs/context';
import * as RunHooks from '../../../state/runs/hooks';
import {useViewer} from '../../../state/viewer/hooks';
import * as ViewHooks from '../../../state/views/hooks';
import {useRunSets} from '../../../state/views/runSet/hooks';
import {DateFormat, format} from '../../../util/date';
import {
  EMPTY_PANEL_BANK_CONFIG,
  EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT,
} from '../../../util/panelbankConfigs';
import {getFullWidthPanelLayout} from '../../../util/panelbankGrid';
import {getPanelSpec} from '../../../util/panelTypes';
import * as Report from '../../../util/report';
import {truncateString} from '../../../util/runhelpers';
import {TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {useDefinedMetricContext} from '../../PanelBank/DefinedMetricContext';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {useSemanticLegendSettingsContext} from '../../WorkspaceDrawer/Settings/semanticLegend/SemanticLegendSettingsContextProvider';
import {RunColorOptions, Settings} from '../../WorkspaceDrawer/Settings/types';
import {usePanelContext} from '../PanelContextProvider';
import {ShareReportTriggerWrapper} from './ShareReportTriggerWrapper';

// report names are limited to 128 chars. When editing report titles we prevent
// users from exceeding this. But here, we dont have a limit on the title length
// and title length is used to create the report name. So we need to truncate
// also actual limit is 128, but truncateString uses single char elipses which is
// 3 chars in backend
const MAX_BACKEND_TITLE_LENGTH = 128 - 2;

export const PanelShareInReportModalContainer = ({
  reportID,
  reportName,
  setReportID,
}: {
  reportID: string;
  reportName: string;
  setReportID: (reportID: string | undefined) => void;
}) => {
  const viewer = useViewer();
  const {runSetRefs} = usePanelsSharedRefsContext();
  const {entityName} = RunHooks.useRunSetsQuery({runSetRefs});

  const viewerIsMemberOfTeam = useMemo(
    () => viewer?.teams.edges.some(edge => edge.node.name === entityName),
    [viewer?.teams.edges, entityName]
  );

  const unsetReportID = useCallback(() => {
    setReportID(undefined);
  }, [setReportID]);

  return (
    <ShareReportTriggerWrapper
      reportID={reportID}
      reportName={reportName}
      unsetReportID={unsetReportID}
      userIsMemberOfTeam={viewerIsMemberOfTeam}
    />
  );
};

export const PanelShareInReportMenuItem = ({
  setReportID,
  setReportName,
}: {
  setReportID: (reportID: string) => void;
  setReportName: (reportName: string) => void;
}) => {
  const client = useApolloClient();

  const {definedGlobs: defineMetricGlobDefinitions} = useDefinedMetricContext();

  const {customRunColorsRef, customRunNamesRef, runSetRefs} =
    usePanelsSharedRefsContext();

  const runSets = useRunSets(runSetRefs);
  const customRunColors = ViewHooks.usePart(customRunColorsRef);
  const {semanticLegendSettings, isMetricBasedColors} =
    useSemanticLegendSettingsContext();

  const customRunNames = ViewHooks.usePartMaybe(customRunNamesRef);
  const {mergeFilters, runSetName} = useContext(RunQueryContext);
  const {entityName, projectName} = RunHooks.useRunSetsQuery({runSetRefs});

  const {panel, inheritedSettings, trackPanelControls} = usePanelContext();

  const panelTitle = useMemo(() => {
    const panelSpec = getPanelSpec(panel.viewType);
    if (panelSpec && panelSpec.getTitleFromConfig && panel.config) {
      return panelSpec.getTitleFromConfig(panel.config as any);
    }
    return undefined;
  }, [panel.viewType, panel.config]);

  const sharePanel = useCallback(async () => {
    const report = Report.fromSection(
      {
        runSets,
        customRunColors: customRunColors,
        customRunNames,
        settings: inheritedSettings?.linePlot as Settings,
        panelBankConfig: cloneDeep(EMPTY_PANEL_BANK_CONFIG),
        panelBankSectionConfig: {
          ...cloneDeep(EMPTY_PANEL_BANK_SECTION_CONFIG_FOR_REPORT),
          panels: [{...panel, layout: getFullWidthPanelLayout()}],
        },
        semanticLegendSettings: isMetricBasedColors
          ? {
              ...semanticLegendSettings,
              runColorOptions: RunColorOptions.KeyBasedColors,
            }
          : undefined,
      },
      {mergeFilters, runSetName, noSectionHeader: true},
      undefined,
      defineMetricGlobDefinitions
    );

    const dateFormat = DateFormat.YEAR_MONTH_DAY_HOUR_MINUTE_SECOND;
    const extraCharsLength = 3 + dateFormat.length; // 3 for ' ()'
    const maybeTruncatedPanelTitle =
      panelTitle != null
        ? truncateString(
            panelTitle,
            MAX_BACKEND_TITLE_LENGTH - extraCharsLength,
            TruncationType.Middle
          )
        : panelTitle;
    const name = `${maybeTruncatedPanelTitle} (${format(
      new Date(),
      dateFormat
    )})`;
    const savedView = await saveView({
      client,
      entityName,
      projectName,
      reportName: name,
      draft: false,
      config: report,
      createdUsing: ViewSource.WandbUiSharePanel,
    });
    if (savedView.id == null) {
      toast('Error occured while trying to share panel');
    } else {
      setReportID(savedView.id);
      setReportName(name);
    }

    trackPanelControls({
      action: 'Share panel clicked',
      sharedPanelReportId: savedView.id,
    });

    return savedView;
  }, [
    runSets,
    customRunColors,
    customRunNames,
    inheritedSettings?.linePlot,
    panel,
    isMetricBasedColors,
    semanticLegendSettings,
    mergeFilters,
    runSetName,
    defineMetricGlobDefinitions,
    panelTitle,
    client,
    entityName,
    projectName,
    trackPanelControls,
    setReportID,
    setReportName,
  ]);

  return (
    <DropdownMenu.Item data-test="share-panel-button" onSelect={sharePanel}>
      <Icon name="users-team" /> Share panel in report...
    </DropdownMenu.Item>
  );
};
